import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiPhone, FiMapPin, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import RelatedInventory from '../../../components/RelatedInventory';

const TransmissionRepair = () => {
  const symptoms = [
    'Slipping between gears or delayed engagement',
    'Grinding noises when shifting',
    'Burning smell or red fluid leaks',
    'Check engine light with transmission codes',
    'Shuddering or vibration during acceleration',
    'Won\'t go into gear or stuck in limp mode'
  ];

  const services = [
    { name: 'Diagnostic Scan', desc: 'Computer analysis to identify exact issues', price: '$149' },
    { name: 'Fluid Service', desc: 'Complete flush with filter replacement', price: '$189-249' },
    { name: 'Clutch Repair', desc: 'Manual transmission clutch replacement', price: '$1,200-2,500' },
    { name: 'Rebuild Service', desc: 'Complete internal overhaul', price: '$2,500-4,500' },
    { name: 'CVT Service', desc: 'Specialized CVT maintenance and repair', price: 'Quote needed' },
    { name: 'Differential Service', desc: 'Front/rear differential fluid service', price: '$159-199' }
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen">
      <Helmet>
        <title>Transmission Repair Dallas | Expert Auto & Manual Service | Paradise-Performance</title>
        <meta name="description" content="Dallas transmission repair experts. Automatic, manual, CVT specialists. Free diagnosis with repair. Same-day service available. Call  for honest quotes." />
        <link rel="canonical" href="https://paradise-performance.com/services/transmission-repair" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Paradise-Performance Transmission Repair",
            "url": "https://paradise-performance.com/services/transmission-repair",
            "telephone": "",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            }
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-red-600 to-red-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img 
            src="/assets/assets/images/new/transmission-repair/8transmission-repair.webp"
            alt="Transmission repair service"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-5xl font-bold mb-6">Transmission Repair Dallas</h1>
          <p className="text-xl mb-8 max-w-3xl">
            That grinding noise isn't going away on its own. Whether it's slipping gears, 
            delayed shifts, or complete failure—we fix transmissions others write off as "totaled."
          </p>
          <a href="tel:469-928-8011" className="inline-block bg-white text-red-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100">
            Get Free Diagnosis: (817) 382-2316
          </a>
        </div>
      </section>

      {/* The Truth */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8">Here's What Transmission Shops Won't Tell You</h2>
          <p className="text-lg text-gray-700 mb-6">
            Half the "blown transmissions" we see just need a solenoid pack or valve body repair. 
            But most shops? They'll quote you $4,000 for a rebuild because it's easier money. 
            We actually diagnose first, then fix only what's broken.
          </p>
          <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-600">
            <p className="font-semibold mb-2">Last week's save:</p>
            <p className="text-gray-700">
              2018 F-150 with harsh shifting. Another shop quoted complete rebuild at $3,800. 
              Actual issue? Faulty speed sensor and old fluid. Fixed for $420. That's the 
              difference honest diagnostics makes.
            </p>
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8">Don't Ignore These Warning Signs</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {symptoms.map((symptom, idx) => (
              <div key={idx} className="flex items-start">
                <FiAlertCircle className="text-red-500 mt-1 mr-3 flex-shrink-0" />
                <p className="text-gray-700">{symptom}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-lg font-semibold text-red-600">
            Catch it early = $200-500 fix. Wait too long = $3,000+ rebuild.
          </p>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-12 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/assets/assets/images/new/transmission-repair/Audi-Transmission.webp"
                alt="Audi transmission service"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                <p className="text-white font-bold text-sm">European Transmission Experts</p>
              </div>
            </div>
            <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/assets/assets/images/new/transmission-repair/4transmission-repair.webp"
                alt="Transmission rebuild service"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                <p className="text-white font-bold text-sm">Complete Rebuild Services</p>
              </div>
            </div>
            <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/assets/assets/images/new/transmission-repair/transmission-software-performance-upgrade.webp"
                alt="Transmission software programming"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                <p className="text-white font-bold text-sm">Software & Programming</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services & Pricing */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold mb-8 text-center">Transparent Transmission Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <div key={idx} className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-lg mb-2">{service.name}</h3>
                <p className="text-gray-600 mb-3">{service.desc}</p>
                <p className="text-2xl font-bold text-green-600">{service.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8">Why Dallas Chooses Paradise-Performance</h2>
          <div className="space-y-4">
            <div className="flex items-start">
              <FiCheckCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <strong>Actual Diagnostics:</strong> We use factory-level scanners, not guess work. 
                We find the real problem, not the expensive one.
              </div>
            </div>
            <div className="flex items-start">
              <FiCheckCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <strong>In-House Rebuilds:</strong> We don't ship your transmission out. 
                Our techs rebuild it here, saving you time and money.
              </div>
            </div>
            <div className="flex items-start">
              <FiCheckCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <strong>Warranty That Matters:</strong> 1 year/24,000 mile warranty on rebuilds. 
                We stand behind our work because we do it right.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Stop Driving on a Bad Transmission</h2>
          <p className="text-xl mb-8">Every mile risks turning a small fix into a total failure.</p>
          <a href="tel:469-928-8011" className="inline-block bg-white text-red-600 px-10 py-5 rounded-lg font-bold text-lg hover:bg-gray-100">
            <FiPhone className="inline mr-2" /> Call Now: 
          </a>
          <p className="mt-6"><FiMapPin className="inline mr-2" />305 Co Rd 3470
Paradise, TX 76073</p>
        </div>
      </section>
      {/* Vehicle Inventory Links */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <RelatedInventory limit={4} title="Vehicles For Sale" />
      </div>
    </motion.div>

  );
};

export default TransmissionRepair;
